import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { testoPerCodice } from './mfaTesti';

// FE-001 — estrae token da hash fragment (#token=...). Il fragment non
// viene mai inviato come Referer ne' loggato dai server. Fallback a
// query string per retrocompatibilita' con vecchie email gia' inviate.
function extractMfaToken() {
  const hash = window.location.hash || '';
  if (hash.startsWith('#')) {
    const hashParams = new URLSearchParams(hash.slice(1));
    const t = hashParams.get('token');
    if (t) return t;
  }
  const qs = new URLSearchParams(window.location.search);
  return qs.get('token') || '';
}

export default function VerifyMfa() {
  const { verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [error, setError] = useState('');
  const [codice, setCodice] = useState('');
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    const token = extractMfaToken();
    // Pulizia immediata di URL e hash per rimuovere il token dalla
    // barra indirizzi (non rimuove dalla history del browser).
    if (token) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (!token) {
      setStatus('error');
      setError('Token mancante.');
      return;
    }

    verifyMfa(token)
      .then(() => {
        setStatus('success');
        setTimeout(() => navigate('/'), 1500);
      })
      .catch((err) => {
        setStatus('error');
        // Il CODICE, non la frase: il testo del backend resta come ripiego
        // per un codice che non conosciamo, cosi' un caso nuovo degrada in
        // «messaggio generico» invece che in «schermata vuota».
        setCodice(err.response?.data?.codice || '');
        setError(err.response?.data?.errore || 'Link non valido o scaduto.');
      });
  }, [verifyMfa, navigate]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--chip)'
    }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 16px', textAlign: 'center' }}>
        <div className="gradient-brand" style={{ width: 52, height: 52, borderRadius: 'var(--radius-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 6px 18px rgba(35,38,47,0.08)' }}>
          <span style={{ color: 'white', fontWeight: 500, fontSize: 22, fontStyle: 'italic' }}>m</span>
        </div>
        <div style={{
          background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)',
          padding: 28, boxShadow: '0 6px 18px rgba(35,38,47,0.08)'
        }}>
          {status === 'verifying' && (
            <>
              <div className="skeleton" style={{ width: 48, height: 48, borderRadius: '50%', margin: '0 auto 16px' }} />
              <h2 style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink)', marginBottom: 8, fontFamily: 'var(--font-display)' }}>
                Verifica in corso...
              </h2>
              <p style={{ fontSize: 13, color: 'var(--ds-muted)' }}>
                Stiamo confermando il tuo accesso.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle2 size={48} strokeWidth={2} color="var(--brand)" style={{ margin: '0 auto 16px', display: 'block' }} />
              <h2 style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink)', marginBottom: 8, fontFamily: 'var(--font-display)' }}>
                Accesso confermato
              </h2>
              <p style={{ fontSize: 13, color: 'var(--ds-muted)' }}>
                Reindirizzamento alla dashboard...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle size={48} strokeWidth={2} color="var(--ds-danger)" style={{ margin: '0 auto 16px', display: 'block' }} />
              <h2 style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink)', marginBottom: 8, fontFamily: 'var(--font-display)' }}>
                {testoPerCodice(codice, error).titolo}
              </h2>
              <p style={{ fontSize: 13, color: 'var(--ds-muted)', marginBottom: 16 }}>
                {testoPerCodice(codice, error).spiega}
              </p>
              <button
                onClick={() => navigate('/auth/login')}
                style={{
                  padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none',
                  background: 'var(--brand)', color: '#fff', fontSize: 13,
                  fontWeight: 500, cursor: 'pointer',
                }}
              >
                {testoPerCodice(codice, error).azione}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
